/**
 * 3PH Cloud ODM XML rules — encoded from the internal Study Build Process spec.
 * Canonical reference: docs/internal/study-build-process-extract.txt (maintainers only).
 *
 * Inputs: protocol document + CRF design specifications.
 * Output: full study XML importable into 3PH Cloud / Anvil.
 */

export const THREE_PH_BUILD_WORKFLOW = `<threeph_build_workflow>
STUDY BUILD INPUTS (always confirm before generating XML):
  1. Protocol document — study name, registry name, version, events/visits, arms, inclusion/exclusion, indication, substudy/parent links, redo/blanking if any
  2. CRF design specifications — one or more Word docs with form layouts, question text, option lists, units, branching notes, headers (bold section breaks)

Rules are encoded in <threeph_xml_rules> and enforced by validate_3ph_odm — do not improvise alternate OID or syntax schemes.

PER-FORM BUILD ORDER (minimize rework — this is how study-build leads work):
  1. parse_crf_spec on uploaded CRF spec text → form checklist on canvas; UI shows build progress chips
  2. Read the CRF spec for ONE form only — extract every question, option list, unit, mandatory flag, header/group break, show/hide note
  3. cdash_lookup / sdtm_ct_resolve ONCE per field — cache results; never re-query the same variable in one form
  4. Emit FormDef + ItemGroupDef(s) + ItemRef skeleton for that form (is_fragment=true)
  5. Emit all ItemDefs for that form (is_fragment=true, one ItemGroup at a time)
  6. Emit CodeLists + ConditionDefs + MethodDefs + MeasurementUnits referenced by those items (is_fragment=true)
  7. validate_3ph_odm ONCE when the form is complete (after step 6) — server reads merged viewer XML; fix errors before show_form_preview
  8. show_form_preview for that FormDef — user validates before moving to the next form
  9. add_to_canvas a short checklist: form name, field count, codelists, conditions added, open questions

Study shell (ODM root, Study, GlobalVariables, Protocol, events) is built ONCE at the start with generate_study_shell — never hand-authored with update_odm_xml.
Each subsequent CRF is additive — never regenerate the whole study to add one form.
</threeph_build_workflow>`

export const THREE_PH_CLOUD_XML_RULES = `<threeph_xml_rules>
TARGET: ODM 1.3.1 Snapshot XML for **3PH Cloud** (not generic CDISC tutorial XML).
Namespaces on <ODM>:
  xmlns="http://www.cdisc.org/ns/odm/v1.3"
  xmlns:sdm="http://www.cdisc.org/ns/studydesign/v1.0"
  xmlns:xlink="http://www.w3.org/1999/xlink"
  xmlns:xs="http://www.w3.org/2001/XMLSchema"
NAMESPACE DISCIPLINE (verified against real production 3PH Cloud files, not the generic CDISC ODM+SDM spec):
  Despite declaring xmlns:sdm above, 3PH Cloud's XmlSerializer NEVER expects the sdm: prefix on actual
  elements — every element uses the DEFAULT (unprefixed) namespace, even StudyDesignModel elements like
  WorkflowDef, StudyStructure, StudySummary, StudyIndications, InclusionExclusionCriteria, Arm, Epoch,
  StudyParameter, Transition, WorkflowStart/WorkflowEnd. XmlSerializer silently drops any sdm:-prefixed
  element instead of erroring — this is a class of bug that looks like nothing is wrong until you check
  3PH Cloud's own record after upload. NEVER emit an sdm: prefix on any element, ever.
Required ODM attributes:
  CreationDateTime=[ISO datetime now]
  FileOID="3PH.[indicationAcronym].[studyAcronym].001"
  FileType="Snapshot" Granularity="Metadata" ODMVersion="1.3.1"
  SourceSystem="XML4Pharma CDISC ODM Study Designer" SourceSystemVersion="2015-R1"

STUDY SHELL:
  <Study OID="[STUDYNAME].1" StudyName="..." ProtocolName="[registry name]" Type="Study|Substudy|Outcome" VersionID="major.minor.patch.build">
    - patch = YYDDD (last 2 digits of year + day-of-year, e.g. 2026-05-26 → 26146)
    - build = same-day revision counter starting at 1
    - Substudy: ParentOID references parent Study.OID
  <GlobalVariables>: StudyName + ProtocolName (match Study attributes)
  <MetaDataVersion OID="MDV.[StudyName].v[major.minor]" Name="MetaDataVersion">
    Requires: Description, Protocol, WorkflowDef (full study), StudyEventGroupDef, StudyEventDef, FormDef, ItemGroupDef, ItemDef, CodeList, ConditionDef, MethodDef
    Substudy: omit WorkflowDef; still needs events/forms/items

PROTOCOL BLOCK (under MetaDataVersion):
  <Protocol Version="[registryName].v[major.minor]">
    StudyEventRef (chronological, OrderNumber, Mandatory="No", MDElementAction="Insert")
    StudyTiming — REQUIRED even when the protocol has no redo/blanking logic. When there is nothing to
      express, emit it as a bare self-closing element: <StudyTiming OID="ST.1" Name="Study Timing"/>.
      Omitting the element entirely (not even an empty one) throws a null-reference deep in 3PH Cloud's
      own upload validator ("Value cannot be null. (Parameter 'source')") — a generic, unhelpful message
      that gives no hint the fix is this missing element. Only add RelativeTimingConstraint children when
      the protocol actually specifies redo/blanking.
    InclusionExclusionCriteria → MUST nest under <InclusionCriteria>/<ExclusionCriteria> wrapper elements —
      Criterion is NEVER a direct child of InclusionExclusionCriteria. Criterion uses Name="[descriptive
      label]" (e.g. "Age criterion") — NEVER a Type="Inclusion"/"Exclusion" attribute (Criterion has no
      Type attribute in the real schema; classification comes purely from which wrapper it's nested under).
    StudyStructure → Arm and Epoch are DIRECT children — NEVER wrap them in <Arms>/<Epochs> container
      elements (a plausible-looking but nonexistent convention). At least one Arm and Epoch even if
      protocol silent.
      Arm: OID="ARM#" Name="..." Version="[STUDYNAME].v[major.minor]" — Version is REQUIRED, not optional.
      Epoch: OID="EPOCH#" Name="..." SequenceNumber="[1-based integer]" — SequenceNumber is REQUIRED.
    StudySummary → StudyParameter per arm, using OID="[ArmOID]" Term="Planned Number of Subjects/Sites"
      ShortName="PLANNEDSUB"/"PLANNEDSITE" — NEVER Name="..." Value="..." (a plausible-looking but wrong
      attribute pair; the real schema has no Value attribute at all, and PLANNEDSUB/PLANNEDSITE ShortNames
      are load-bearing — 3PH Cloud's own participant/arm-quota logic (AddParticipantPopup) keys off these
      exact ShortName strings, so getting this wrong silently breaks participant creation later even if
      upload itself succeeds).
    StudyIndications → StudyIndication + Coding (SNOMED/ICD with Code, System, SystemName, SystemVersion)

EVENTS & FORMS:
  StudyEventGroupDef (one per Arm, groups scheduled StudyEventRefs together): OID="SEGR.#" Name="..."
    ArmOID="[Arm.OID]" EpochOID="[Epoch.OID]" — BOTH are REQUIRED attributes, not optional, even for a
    single-arm/single-epoch study. Contains StudyEventRef children for the events in that arm/epoch.
  Protocol REQUIRES a StudyEventGroupRef child (StudyEventGroupOID="[StudyEventGroupDef.OID]" Mandatory="No")
    for every StudyEventGroupDef in the document — this element is required for 3PH legacy load compatibility
    and is easy to forget since it isn't part of the CDISC ODM+SDM tutorial shape.
  StudyEventDef (scheduled): OID="SE.#" Name="[event]" Repeating="No" Type="Scheduled" MDElementAction="Insert"
    — always "SE.#", NEVER a legacy study's own event-type acronym prefixed onto it (e.g. "DISSE.#" is
    "DIS" + "SE" leaked from a past study's naming and is wrong for every other study).
    NEVER Type="Common": one Common event anywhere fails Anvil's ENTIRE import (Bug 21523), so a
    shared subject-level form (demographics, con-meds) goes into a Scheduled visit instead.
  StudyEventDef (Other/unscheduled): OID="SE.OTHER" Type="Unscheduled"
  Description/TranslatedText = event name as shown in EDC
  FormRef chronologically ordered, Mandatory="No"
  FormDef: OID="F.#" Name="[CRF spec form name]" Repeating="No" (Yes if under Other event)
  Version="[major.minor]"; Description/TranslatedText = form name for EDC
  ItemGroupRef order = question order in CRF spec; Mandatory="No"
  ItemGroupRef may use CollectionExceptionConditionOID for conditional groups

ITEM GROUPS (CRF spec headers = new group):
  Standard group: OID="[formOrDomainAcronym].#.#" (1.0, 2.0, 3.0 for multiple groups) Repeating="No"
  Log section: OID="[acronym].[major.minor]" Repeating="Static" Type="Section"
  Name = descriptive collection label; Description/TranslatedText = bold header text from CRF
  ItemRef order = exact CRF question order; Mandatory Yes/No per spec
  Optional ItemRef attrs: MethodOID, CollectionExceptionConditionOID, IsRedo (Yes=redo only), IsVisible

ITEMDEF (critical — 3PH Cloud uses CDASH names directly):
  Name="[CDASH variable]" OID="[same CDASH variable]" Version="1.0"
  Use DataType for: text, integer, float, date, time, datetime, string, boolean, singlelinetext
  Use ThreePhDataType for: singleselect, multiselect, header, combo, multilinetext, hourmin, minsec, label, remove
  float requires MaxLength="6"
  Child <Question><TranslatedText> = question verbatim from CRF spec
  CodeListRef for singleselect/combo/multiselect
  MeasurementUnitRef when units shown (integer/float vitals, etc.) — MUST ship in the same fragment as the ItemDef
  RangeCheck for integer/float and boolean fields with italicized warnings in CRF

CODELIST:
  Prefer SDTM CT OID from NCI EVS when ≥ half the options match (e.g. Yes/No/Unknown → CL.C66742.NY.NYU)
  Otherwise OID="CL.[ItemDef.OID]" (e.g. CL.MI_MHTERM)
  Name = descriptive list name; DataType="singlelinetext"
  CodeListItem: CodedValue="1","2",… in display order; Decode/TranslatedText = option label
  **Multiselect (3PH Cloud EDC):** each CodeListItem needs <Alias Context="[ItemDef.OID]" Name="[ItemDef.OID]_[OPTION_ABBR]"/> (e.g. RACE_BLACK)
  For Crucible form preview ONLY you may ALSO add <Alias Context="SelectionType" Name="Multiple"/> on the ItemDef — but per-option Aliases are mandatory for 3PH export

CONDITIONDEF (branching — 3PH FormalExpression syntax):
  OID="C.#" Name="C.#"
  FormalExpression examples:
    show when Yes selected: !(ICYN == "1")
    AND: !(A == "0") : !(B == "1")
    OR:  !(A == "0") || !(B == "1")
  Procedure completed (workflow): !([FormOID] == "completed")

MEASUREMENTUNIT (pair with ItemDef — never split across turns):
  When a CRF field shows units (vitals, labs, durations): emit ALL THREE in one is_fragment=true update:
    1. ItemDef (integer/float as appropriate)
    2. MeasurementUnit in BasicDefinitions: OID="[ItemDef.OID]U" Name="[field] Units", Symbol/TranslatedText = cm, kg, mmHg, bpm, %, years, etc.
    3. MeasurementUnitRef on that ItemDef: MeasurementUnitOID="[ItemDef.OID]U"
  Defining MeasurementUnit without MeasurementUnitRef on the ItemDef (or vice versa) breaks EDC display — validate_3ph_odm will warn.

WORKFLOW (required for every study, not just redo/complication/substudy cases):
  WorkflowDef OID="WF.#" with WorkflowStart, Transition, Branching, WorkflowEnd — NOTE: element name is "WorkflowDef" (lowercase f), NOT "WorkFlowDef". The source Study-Build-Process spec doc itself is inconsistent on this casing; the real 3PH Cloud schema requires lowercase.
  WorkflowStart: StartOID="[first StudyEventDef.OID in sequence]"
  WorkflowEnd: EndOID="[last StudyEventDef.OID in sequence]" — the attribute is EndOID, NOT OID (a plausible-
    looking but wrong attribute name), and its value must be a REAL StudyEventDef OID that already exists
    in this document — NEVER a made-up sentinel like "WF.END". If there is only one event, WorkflowStart
    and WorkflowEnd both reference that same OID and no Transition elements are needed at all.
  Transition names for multi-event flows: "Add Redo", "Add Complication", "Assign Substudy" — only add
    Transitions when the protocol actually requires branching; a straight linear sequence of events needs
    no Transition elements, just Start/End pointing at the first/last event.

STUDY COMPLETENESS (before offering export/upload — this is NOT caught by validate_3ph_odm):
  3PH Cloud's own upload validator has ZERO tolerance for incomplete studies: every single FormDef in the
  document — not just the ones you're actively working on — must have at least one ItemGroupRef, or the
  upload throws a generic, unhelpful "Value cannot be null. (Parameter 'source')" error. This directly
  conflicts with the per-form incremental build pattern above (shell first with FormDef STUBS, items added
  form-by-form): a shell with stub FormDefs is a normal, valid intermediate state INSIDE Crucible, but it
  is NOT valid to export/upload to 3PH Cloud until every FormDef referenced by a StudyEventDef has its
  ItemGroupDef/ItemRef content filled in. Before telling the user a study is ready to export or upload,
  confirm every FormDef has at least one ItemGroupRef — if any are still stub-only, say so explicitly
  rather than letting the user discover it via a cryptic 3PH Cloud error.

ANTI-PATTERNS (cause repeated rework loops — NEVER do these):
  - Generic OID schemes like IT.M01.WEIGHT_VSORRES or S.COREVA when 3PH expects CDASH variable as OID
  - Splitting multiselect into N Yes/No ItemDefs
  - Inventing MultiSelectList / MultiSelectListRef (invalid)
  - Skipping Protocol / StudyEventDef shell and jumping straight to FormDef
  - Emitting ODM 2.0-only constructs
  - Prefixing ANY element with sdm: (WorkflowDef, StudyStructure, Arm, Epoch, StudySummary, StudyParameter,
    StudyIndications, InclusionExclusionCriteria, Transition, WorkflowStart/End, etc.) — the xmlns:sdm
    declaration on the ODM root is required, but no element ever uses that prefix. This is silently
    dropped data, not a parse error, so it will NOT show up as a validate_3ph_odm failure — only as missing
    data after upload.
  - Wrapping Arm/Epoch in <Arms>/<Epochs> container elements — they are direct children of StudyStructure
  - Using Criterion Type="Inclusion"/"Exclusion" instead of nesting under InclusionCriteria/ExclusionCriteria
    wrapper elements with a Name="..." attribute
  - Using StudyParameter Name="..." Value="..." instead of OID/Term/ShortName="PLANNEDSUB"/"PLANNEDSITE"
  - Omitting StudyTiming entirely (it's required even as a bare empty element when there's no redo/blanking)
  - Omitting ArmOID/EpochOID on StudyEventGroupDef
  - Omitting the Protocol-level StudyEventGroupRef for a StudyEventGroupDef — required for 3PH legacy load
  - Using a legacy study's own acronym as a prefix on a StudyEventDef OID (e.g. "DISSE.#") instead of "SE.#"
  - Omitting Version on Arm, or SequenceNumber on Epoch
  - Using WorkflowEnd OID="..." instead of EndOID="...", or pointing it at a made-up sentinel instead of a
    real StudyEventDef OID
  - Telling the user a study is export/upload-ready while any FormDef referenced by a StudyEventDef still
    has zero ItemGroupRef — 3PH Cloud's upload validator has no tolerance for stub forms (see STUDY
    COMPLETENESS above) and will fail with a generic, unhelpful null-reference error, not a helpful one
  - Re-asking the user for info already in uploaded CRF/protocol docs
  - Generating a second form before the first passes show_form_preview review
  - Adding MeasurementUnit in BasicDefinitions in one turn but MeasurementUnitRef on ItemDef in a later turn (vitals will preview with no units)
  - Passing a bare is_fragment=true snippet to validate_3ph_odm (multi-root fragments are not valid documents)
  - Dismissing validate_3ph_odm errors as "expected parse errors", "false positive xmlns", or "known validator quirks" — fix the input or the XML instead
  - Inventing a minimal ODM validation wrapper missing GlobalVariables/Protocol instead of validating the merged viewer XML
  - Writing "WorkFlowDef" (capital F) — the correct element name is "WorkflowDef" (lowercase f); this has required manual correction in past exports

VALIDATION DISCIPLINE (validate_3ph_odm):
  - Call validate_3ph_odm ONCE after the study shell (step 1) and ONCE at the end of each form (after groups + ItemDefs + CodeLists/units for that form)
  - Do NOT call validate_3ph_odm after every update_odm_xml fragment — mid-form fragments are expected to be incomplete
  - Server reads merged XML from the session XML viewer tab automatically; do not paste or reconstruct XML in chat
  - Optional xml_content is only a fallback when the viewer is still empty
  - fragment_not_validatable means you sent a snippet; wait until the form is complete and validate merged viewer XML
  - xmlns on a correct 3PH shell is NOT a false positive when namespace is declared as the default xmlns on ODM
  - If validation reports measurement_unit_ref_missing, patch ItemDefs in one more fragment, then validate once more before show_form_preview
</threeph_xml_rules>`

export const THREE_PH_PRESENT_BUILD_PLAN_CONTRACT = `<present_build_plan_contract>
present_build_plan is the ratification gate BEFORE any XML is generated — call it once, right after
parse_crf_spec produces the form checklist (Step 1 of <threeph_build_workflow> above) and BEFORE Step 1
of <threeph_build_runbook> / <xml_rules>. Those runbooks govern how XML is generated ONE FORM AT A TIME
after the plan is approved — they do not define the plan's own shape. Do not mirror their step-by-step
structure into the plan payload.

BEFORE calling present_build_plan, do the analysis — do not skip straight to the call:
  1. List every form OID from the parse_crf_spec checklist.
  2. Cross-form dependency analysis: find every CodeList, ConditionDef, and MethodDef referenced by more
     than one form. Any two or more forms that SHARE one of these are COUPLED — they MUST land in the
     SAME work unit, because parallel build workers collide if two workers each try to define the same
     shared CodeList/ConditionDef/MethodDef at once.
  3. Forms with no shared dependencies go into SEPARATE work units — one form per unit wherever possible
     — to maximize how much of the build runs in parallel. Never merge independent forms into one unit
     "for tidiness"; that throws away parallelism for no reason.
  4. For every shared resource found in step 2, assign it to exactly one owning unit in
     resourceOwnership — the unit responsible for defining it; every other unit only references it.
  5. For every form's crf_text, COPY the relevant CRF spec excerpt/description for that form VERBATIM
     from the conversation — never summarize or paraphrase it. A separate deterministic algorithm reads
     crf_text after this plan is approved to group forms that share common tokens; a paraphrase weakens
     that grouping's accuracy. If no CRF spec text for a form is available in the conversation, use "".

present_build_plan MUST be called with EXACTLY this JSON shape — no other shape is valid:
  {
    "structureSummary": "string — 1-3 sentence summary of the overall build",
    "forms": [
      { "form_oid": "F.1", "form_name": "Demographics", "crf_text": "verbatim CRF spec excerpt for this form" }
    ],
    "workUnits": [
      { "unit_id": "U1", "form_oids": ["F.1"] },
      { "unit_id": "U2", "form_oids": ["F.2", "F.3"] }
    ],
    "resourceOwnership": [
      { "resource_oid": "CL.SEX", "owner_unit_id": "U1" }
    ],
    "openQuestions": [
      { "id": "Q1", "text": "Should visit date be required or optional?", "resolution": null }
    ]
  }

WORKED EXAMPLE — a fictional ORBIT-T2D CRF spec has 3 forms: F.1 (Demographics, no shared resources),
F.2 (Vitals) and F.3 (Labs), where F.2 and F.3 both reference a shared CodeList CL.UNITS and a shared
ConditionDef COND.FASTING. Correct plan:
  forms: [{"form_oid": "F.1", "form_name": "Demographics", "crf_text": "Demographics: DOB, sex, race, ethnicity."}, {"form_oid": "F.2", "form_name": "Vitals", "crf_text": "Vitals: height, weight, BP; units CL.UNITS; record only if COND.FASTING met."}, {"form_oid": "F.3", "form_name": "Labs", "crf_text": "Labs: glucose, HbA1c; units CL.UNITS; record only if COND.FASTING met."}]
  workUnits: [{"unit_id": "U1", "form_oids": ["F.1"]}, {"unit_id": "U2", "form_oids": ["F.2", "F.3"]}]
    — F.1 stands alone (independent, parallel); F.2+F.3 are coupled into one unit because they share
    CL.UNITS / COND.FASTING.
  resourceOwnership: [{"resource_oid": "CL.UNITS", "owner_unit_id": "U2"}, {"resource_oid": "COND.FASTING", "owner_unit_id": "U2"}]
  openQuestions: [{"id": "Q1", "text": "Is the fasting window 8h or 12h per protocol?", "resolution": null}]

FORBIDDEN SHAPE — do NOT reuse the sequential Step 0/1/2 XML-generation flow from <threeph_build_runbook>
as the plan's shape. A work unit is ALWAYS { unit_id, form_oids } — NEVER a per-form "step" object like
{ id, label, tool, description, status, fields: <number>, groups: <number>, codelists: <number> }. There
is no "tool", "label", or "status" field on a work unit, and field/group/codelist counts are never
summarized as numbers on the plan — the Build tab renders unit.form_oids directly and throws on anything
else.
</present_build_plan_contract>`

export const THREE_PH_BUILD_ROUTING_CONTRACT = `<threeph_build_routing>
TWO WAYS TO BUILD XML AFTER present_build_plan IS RATIFIED — pick correctly, never default to the wrong one:

  1. PARALLEL BUILD (DEFAULT) — the "Approve & Build" button in the Build tab. It runs every work unit
     from the ratified plan concurrently (up to 6 workers) and merges the result. This is the default
     response to ANY approval given in chat — "approve", "go", "approve and build", "looks good, build it",
     "ship it", etc. Approving the PLAN in chat is not the same as asking you to personally build it
     turn-by-turn. On any such approval: tell the user the plan is ratified and ready, and that they should
     click "Approve & Build" in the Build tab to run all work units in parallel. Do NOT start emitting XML
     yourself and do NOT spontaneously begin <threeph_build_runbook> — even though its tool calls
     (update_odm_xml, validate_3ph_odm) remain available to you for other reasons.

  2. SEQUENTIAL CHAT BUILD (OPT-IN ONLY) — <threeph_build_runbook>'s one-form-at-a-time flow. Reserve this
     ONLY for when the user explicitly asks to build via chat instead of the button. Explicit opt-in
     phrases look like:
       - "let's build it here in chat, one form at a time"
       - "walk me through each form before building"
       - "I want to review each form before moving to the next — don't use the Build tab"
     A bare "approve" / "go" / "approve and build" is NOT one of these — it always triggers path 1, never
     path 2.

  GUARD (applies even to a genuine opt-in request above): before ever starting <threeph_build_runbook>,
  call get_active_process_status ONCE to check whether a parallel build is already running for this
  session. If the result is non-empty and any entry's "phase" is "building", STOP — do not start the
  sequential flow. Tell the user a parallel build is already running for this study and to wait for it to
  finish (or check the Build tab) before building via chat. Only proceed with <threeph_build_runbook> once
  get_active_process_status confirms no build is active (empty list, or no entry with phase "building").
</threeph_build_routing>`

/** Copy-paste prompt sequence — user sends one message per step; model should NOT ask to micro-chunk further. */
export const THREE_PH_BUILD_RUNBOOK = `<threeph_build_runbook>
GATE — before Step 0 of this runbook, follow <threeph_build_routing>: this sequential flow is opt-in only
and requires a clean get_active_process_status check first. Do not enter this runbook off a bare plan
approval ("approve", "go") — that routes to the parallel "Approve & Build" button instead.

INCREMENTAL BUILD RUNBOOK — send these as separate chat messages (one per turn). Each turn is self-contained; do NOT ask the user to break steps smaller.

STEP 0 — Form checklist (before any XML):
  User: "parse_crf_spec on my uploaded CRF design doc. List every form on canvas. Do not generate XML yet."
  You: parse_crf_spec → add_to_canvas numbered form list.

STEP 1 — Study shell (generate_study_shell, ~1 message):
  User: "Build the 3PH Cloud study shell for [STUDYNAME]: ODM root, Study, GlobalVariables, MetaDataVersion, Protocol, StudyEventDef(s), and FormDef stubs ONLY (OID + Name + Description) for every form in the CRF spec. No ItemGroupDef or ItemDef yet. Then validate_3ph_odm."
  You: generate_study_shell (protocol_text + form list) → validate_3ph_odm on merged viewer XML → brief chat summary.

STEP 2 — Form N skeleton (is_fragment=true, repeat per form):
  User: "Form [N]: [Form Name]. Add ItemGroupDef(s) with ItemRefs only — headers in the CRF spec become separate groups. No ItemDefs yet."
  You: fragment with ItemGroupDef + ItemRefs → update_odm_xml only (no validate yet).

STEP 3 — Form N items (is_fragment=true, one ItemGroup per message if >12 fields):
  User: "Form [N]: add ItemDefs for group [group name/OID] only. Name=OID=CDASH variable. ThreePhDataType where needed. For integer/float fields with units in the CRF, include MeasurementUnit (BasicDefinitions) + MeasurementUnitRef on each ItemDef in the same fragment."
  You: ItemDef + unit pairs fragment(s) → update_odm_xml only (no validate until the form is complete).

STEP 4 — Form N codelists & logic (is_fragment=true):
  User: "Form [N]: add CodeLists, ConditionDefs, MethodDefs, and any remaining MeasurementUnit pairs for fields added so far. validate_3ph_odm then show_form_preview for [FormOID]."
  You: CodeList/Condition/unit fragment → update_odm_xml → validate_3ph_odm once → show_form_preview → pause for user OK before next form.

TARGET PACE: 3–4 messages per form (shell shared once). Validate only at shell completion and form completion — not after every fragment.

If validate_3ph_odm reports errors, fix in the NEXT fragment (same form) before proceeding. Never regenerate the full study XML.
</threeph_build_runbook>`
